import { useMemo } from 'react';
import { doc } from 'firebase/firestore';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import firebaseApp from '../../../../firebase-app';
import { getFotbollPlayTeamConfigPath } from '../../../../utils/paths';

interface FotbollPlayTeamChannel {
  id?: unknown;
  channelId?: unknown;
  channel?: unknown;
  value?: unknown;
  name?: unknown;
  title?: unknown;
}

interface FotbollPlayTeamConfig {
  channels?: unknown;
}

export interface FotbollPlayChannelOption {
  id: string;
  title: string;
}

function toChannelOption(value: unknown, fallbackId?: string): FotbollPlayChannelOption | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const id = String(value);
    return { id, title: id };
  }

  if (!value || typeof value !== 'object') {
    if (fallbackId) {
      return { id: fallbackId, title: fallbackId };
    }
    return null;
  }

  const channel = value as FotbollPlayTeamChannel;
  const id =
    channel.id ??
    channel.channelId ??
    channel.channel ??
    channel.value ??
    fallbackId ??
    channel.name ??
    channel.title;

  if (typeof id !== 'string' && typeof id !== 'number') {
    return null;
  }

  const channelId = String(id);
  const title =
    typeof channel.title === 'string'
      ? channel.title
      : typeof channel.name === 'string'
        ? channel.name
        : channelId;

  return { id: channelId, title };
}

function toChannelId(value: unknown): string | null {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const channel = value as FotbollPlayTeamChannel;
  const channelId =
    channel.id ??
    channel.channelId ??
    channel.channel ??
    channel.value ??
    channel.name ??
    channel.title;

  return typeof channelId === 'string' || typeof channelId === 'number' ? String(channelId) : null;
}

function getFotbollPlayTeamChannelIds(config?: FotbollPlayTeamConfig): string[] {
  const channels = config?.channels;

  if (Array.isArray(channels)) {
    return Array.from(new Set(channels.map(toChannelId).filter((id): id is string => !!id)));
  }

  if (channels && typeof channels === 'object') {
    return Array.from(
      new Set(
        Object.entries(channels).flatMap(([key, value]) => {
          if (value === true) return [key];
          const channelId = toChannelId(value);
          return channelId ? [channelId] : [];
        })
      )
    );
  }

  return [];
}

function getFotbollPlayTeamChannelOptions(
  config?: FotbollPlayTeamConfig
): FotbollPlayChannelOption[] {
  const channels = config?.channels;
  const optionsById = new Map<string, FotbollPlayChannelOption>();

  function addOption(option: FotbollPlayChannelOption | null) {
    if (!option) return;
    optionsById.set(option.id, option);
  }

  if (Array.isArray(channels)) {
    for (const channel of channels) {
      addOption(toChannelOption(channel));
    }
  } else if (channels && typeof channels === 'object') {
    for (const [key, value] of Object.entries(channels)) {
      if (value === true) {
        addOption({ id: key, title: key });
        continue;
      }
      addOption(toChannelOption(value, key));
    }
  }

  return Array.from(optionsById.values());
}

export function useFotbollPlayTeamChannels(teamAbbrv: string) {
  const [config, loading, error] = useDocumentData<FotbollPlayTeamConfig>(
    teamAbbrv ? doc(firebaseApp.db, getFotbollPlayTeamConfigPath(teamAbbrv)) : null
  );

  const channels = useMemo(() => getFotbollPlayTeamChannelIds(config), [config]);
  const channelOptions = useMemo(() => getFotbollPlayTeamChannelOptions(config), [config]);

  return {
    channels,
    channelOptions,
    error,
    loading,
  };
}
